import React from 'react';
import './NewHome.css';

/**
 * Conceptual Cloud-Native Architecture.
 *
 * This is a GENERIC, illustrative diagram of a typical cloud-native system —
 * NOT the architecture of SAP Emarsys or any employer, and it contains no
 * proprietary, production, or internal configuration detail. Every relationship
 * shown is a standard, well-known pattern (request path, async fan-out via a
 * broker, managed SQL, IaC provisioning, metrics scraping).
 */
const Architecture = () => {
    return (
        <section id="architecture" className="section">
            <div className="container">
                <p className="eyebrow">04 — Conceptual Cloud-Native Architecture</p>
                <h2 className="section-title">How I think about systems</h2>

                <p className="arch-lede">
                    A conceptual, non-confidential view of a cloud-native system.
                    Clients reach stateless API services behind Kubernetes;
                    long-running work is handed off asynchronously through a Pub/Sub
                    broker so request latency stays low and spikes absorb into the
                    queue. Services persist to managed PostgreSQL (Cloud SQL),
                    infrastructure is provisioned as code, and everything emits
                    metrics to an observability stack.
                </p>

                <figure className="arch-figure">
                    <svg
                        className="arch-svg"
                        viewBox="0 0 760 360"
                        role="img"
                        aria-labelledby="arch-title arch-desc"
                        preserveAspectRatio="xMidYMid meet"
                    >
                        <title id="arch-title">Conceptual cloud-native architecture diagram</title>
                        <desc id="arch-desc">
                            Clients send requests to API services running as Kubernetes
                            workloads. API services read and write to a managed
                            PostgreSQL (Cloud SQL) database for synchronous data, and
                            publish events to a Pub/Sub message broker for asynchronous
                            work. Worker workloads subscribe to the broker and process
                            jobs independently of the request path, also persisting to
                            the database. Infrastructure automation provisions the
                            Kubernetes platform, and an observability and monitoring
                            stack collects metrics from the API and worker workloads.
                        </desc>

                        <defs>
                            <marker id="arch-arrow" viewBox="0 0 10 10" refX="9" refY="5"
                                markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                                <path d="M0 0 L10 5 L0 10 z" fill="var(--text-mute)" />
                            </marker>
                        </defs>

                        {/* Kubernetes boundary */}
                        <rect x="150" y="40" width="340" height="220" rx="10"
                            className="arch-zone" />
                        <text x="162" y="62" className="arch-zone-label">Kubernetes workloads</text>

                        {/* Nodes */}
                        <g className="arch-node">
                            <rect x="20" y="130" width="100" height="56" rx="8" />
                            <text x="70" y="162">Clients</text>
                        </g>
                        <g className="arch-node arch-node-accent">
                            <rect x="178" y="130" width="120" height="56" rx="8" />
                            <text x="238" y="155">API services</text>
                            <text x="238" y="172" className="arch-node-sub">app / REST</text>
                        </g>
                        <g className="arch-node">
                            <rect x="340" y="80" width="120" height="56" rx="8" />
                            <text x="400" y="105">Pub/Sub</text>
                            <text x="400" y="122" className="arch-node-sub">broker</text>
                        </g>
                        <g className="arch-node">
                            <rect x="340" y="180" width="120" height="56" rx="8" />
                            <text x="400" y="205">Workers</text>
                            <text x="400" y="222" className="arch-node-sub">async jobs</text>
                        </g>
                        <g className="arch-node">
                            <rect x="540" y="130" width="120" height="56" rx="8" />
                            <text x="600" y="155">Cloud SQL</text>
                            <text x="600" y="172" className="arch-node-sub">PostgreSQL</text>
                        </g>
                        <g className="arch-node">
                            <rect x="20" y="40" width="120" height="48" rx="8" />
                            <text x="80" y="69">IaC automation</text>
                        </g>
                        <g className="arch-node">
                            <rect x="540" y="280" width="120" height="48" rx="8" />
                            <text x="600" y="309">Observability</text>
                        </g>

                        {/* Edges */}
                        <g className="arch-edge" markerEnd="url(#arch-arrow)">
                            <line x1="120" y1="158" x2="176" y2="158" />
                            <line x1="298" y1="150" x2="340" y2="112" />
                            <line x1="298" y1="166" x2="538" y2="158" />
                            <line x1="400" y1="136" x2="400" y2="178" />
                            <line x1="460" y1="210" x2="538" y2="170" />
                        </g>
                        {/* Provisioning + metrics (dashed, non-data-path) */}
                        <g className="arch-edge arch-edge-dash" markerEnd="url(#arch-arrow)">
                            <line x1="80" y1="88" x2="150" y2="135" />
                            <line x1="298" y1="186" x2="540" y2="300" />
                            <line x1="430" y1="236" x2="545" y2="292" />
                        </g>
                    </svg>
                    <figcaption className="arch-caption">
                        Illustrative only — a generic pattern, not any employer's system.
                    </figcaption>
                </figure>

                <ul className="arch-notes">
                    <li>
                        <strong>Reliability.</strong> Workloads are stateless and
                        replicated; the broker decouples producers from consumers so a
                        slow or failed worker doesn't take down the request path.
                    </li>
                    <li>
                        <strong>Scalability.</strong> API and worker tiers scale
                        independently — bursts land in the queue and drain at the rate
                        consumers can sustain.
                    </li>
                    <li>
                        <strong>Asynchronous communication.</strong> Non-urgent work is
                        published as events rather than handled inline, keeping request
                        latency low and absorbing spikes.
                    </li>
                </ul>
            </div>
        </section>
    );
};

export default Architecture;
